import React, { useState, useEffect } from 'react'
import { Mail, Phone, MapPin, Briefcase, UserCircle, Edit2, User } from 'lucide-react'
import { TeamMember, Role } from '../../services/orgChartService'

// Format address for display - uses structured data if available, otherwise cleans up label
function formatLocationDisplay(location: TeamMember['location']): string {
  if (!location) return ''
  
  // If we have structured data, build a clean display
  if (location.structured) {
    const s = location.structured
    const parts: string[] = []
    
    // Street address (already formatted as "number street")
    if (s.streetAddress) parts.push(s.streetAddress)
    
    // Locality (city/suburb)
    if (s.locality) parts.push(s.locality)
    
    // Region/State (abbreviated if long)
    if (s.region) {
      // Keep it short for cards
      parts.push(s.region)
    }
    
    // Postcode
    if (s.postalCode) parts.push(s.postalCode)
    
    return parts.join(', ')
  }
  
  // Fallback: clean up the raw label
  // Fix pattern like "4, Williamson Avenue" -> "4 Williamson Avenue"
  let label = location.label || ''
  label = label.replace(/^(\d+),\s*/, '$1 ')
  
  return label
}

function formatPhoneHref(phone: string): string {
  const trimmed = phone.trim()
  if (!trimmed) return ''
  const hasPlus = trimmed.startsWith('+')
  const digits = trimmed.replace(/[^\d]/g, '')
  if (!digits) return ''
  return `tel:${hasPlus ? '+' : ''}${digits}`
}

function MemberAvatar({
  member,
  sizeClass,
  iconClass
}: {
  member: TeamMember
  sizeClass: string
  iconClass: string
}) {
  const [photoFailed, setPhotoFailed] = useState(false)
  const photoUrl = member.thumbnailPhotoUrl || null
  const showPhoto = !!photoUrl && !photoFailed

  useEffect(() => {
    setPhotoFailed(false)
  }, [photoUrl])

  if (showPhoto) {
    return (
      <img
        src={photoUrl}
        alt={member.name.fullName}
        className={`${sizeClass} rounded-full object-cover`}
        onError={() => setPhotoFailed(true)}
      />
    )
  }

  return (
    <div className={`${sizeClass} rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0`}>
      <User className={`${iconClass} text-gray-500`} />
    </div>
  )
}

interface PersonCardProps {
  member: TeamMember
  roles?: Role[]
  compact?: boolean
  onClick?: () => void
  onEdit?: () => void
  showEditButton?: boolean
  /** When true, show a badge that this person is hidden from directory/location (admin viewing “show hidden”). */
  showExcludedFromDirectoryBadge?: boolean
}

export const PersonCard: React.FC<PersonCardProps> = ({
  member,
  roles = [],
  compact = false,
  onClick,
  onEdit,
  showEditButton = false,
  showExcludedFromDirectoryBadge = false
}) => {
  const phoneHref = member.phone ? formatPhoneHref(member.phone) : ''

  if (compact) {
    return (
      <div 
        className={`flex items-center space-x-3 p-2 rounded-lg ${onClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={onClick}
      >
        <MemberAvatar member={member} sizeClass="w-8 h-8" iconClass="w-4 h-4" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{member.name.fullName}</p>
            {showExcludedFromDirectoryBadge && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-800">
                Hidden
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{member.primaryEmail}</p>
        </div>
      </div>
    )
  }

  return (
    <div 
      className={`bg-white rounded-lg border border-gray-200 p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start space-x-4">
        <MemberAvatar member={member} sizeClass="w-12 h-12" iconClass="w-6 h-6" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {member.name.fullName}
              </h3>
              {showExcludedFromDirectoryBadge && (
                <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                  Hidden from directory
                </span>
              )}
            </div>
            {showEditButton && onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                title="Edit location"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {member.jobTitle && (
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <Briefcase className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="truncate">{member.jobTitle}</span>
            </div>
          )}

          <div className="flex items-center text-sm text-gray-500 mt-1">
            <Mail className="w-4 h-4 mr-1 flex-shrink-0" />
            <span className="truncate">{member.primaryEmail}</span>
          </div>

          {member.phone && (
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <Phone className="w-4 h-4 mr-1 flex-shrink-0" />
              {phoneHref ? (
                <a
                  href={phoneHref}
                  onClick={(event) => event.stopPropagation()}
                  className="text-purple-600 hover:text-purple-700 hover:underline"
                >
                  {member.phone}
                </a>
              ) : (
                <span>{member.phone}</span>
              )}
            </div>
          )}

          {member.reportsToEmail && (
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <UserCircle className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="truncate">Reports to: {member.reportsToEmail.split('@')[0]}</span>
            </div>
          )}

          {member.location?.label && (
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="truncate">{formatLocationDisplay(member.location)}</span>
              {member.location.type && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  member.location.type === 'office' ? 'bg-blue-100 text-blue-700' :
                  member.location.type === 'remote' ? 'bg-green-100 text-green-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {member.location.type}
                </span>
              )}
            </div>
          )}

          {/* Roles this person holds */}
          {roles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {roles.map(role => (
                <span 
                  key={role.id}
                  className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full"
                >
                  {role.title}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
